import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { submitOrderFailed, toggleSubmitOrderLoading } from '@redux/actions/checkoutProcess';
import { postAsObservable } from '@services/genericService';
import { DELIVERY_OPTION } from '@utils/constant';
import { bopisThresholdCalculate } from '@utils/utility';

const useGetCartValue = () => {
  const currentDefaultCartData = useSelector(state => state.productReducer.cartDetailReducer?.currentCart);
  const clientSOCartData = useSelector(state => state.personalizeOfferReducer.getClientSOCartReducer.data?.OsimCart);

  return useMemo(() => clientSOCartData || currentDefaultCartData, [currentDefaultCartData, clientSOCartData]);
};

const useGetListStoreOutOfStock = () => {
  const cartValue = useGetCartValue();

  return useCallback(() => {
    const endpoint = '/Catalog/GetListStoreProductOutOfStock';
    const cartLines = cartValue?.Lines || [];
    const selectedAddress = cartLines?.[0]?.Address;

    let productsList = [];
    for (const line of cartLines) {
      if (!line.SubLines?.length) {
        productsList.push({
          ProductId: line?.ProductId,
          VariantId: line?.VariantId,
          Threshold: bopisThresholdCalculate({
            lineQuantity: line?.Quantity,
            currentThreshold: line?.BOPISThreshold
          })
        });
      } else {
        line.SubLines.forEach((subLineItem) => productsList.push({
          ProductId: subLineItem?.ProductId,
          VariantId: subLineItem?.VariantId,
          Threshold: bopisThresholdCalculate({
            lineQuantity: subLineItem?.Quantity,
            currentThreshold: subLineItem?.BOPISThreshold
          })
        }));
      }
    }

    const payload = {
      Outlet: selectedAddress?.PickUpStoreId,
      ListProducts: productsList
    };

    return new Promise((resolve, reject) => {
      return from(postAsObservable(endpoint, { ListStoreProducts: [{ ...payload }] }))
        .pipe(map((res) => resolve(res)), catchError((error) => reject(error || 'Failed')))
        .subscribe();
    });
  }, [cartValue]);
};

const useCheckingBOPISOrder = () => {
  const dispatch = useDispatch();
  const getListStoresOutOfStock = useGetListStoreOutOfStock();
  const cartValue = useGetCartValue();
  const settingGlobal = useSelector((state) => state.settingGlobalReducer.settingGlobal);

  return useCallback(async (callback) => {
    const firstLineInCart = cartValue?.Lines?.[0];
    const deliveryOptionsList = firstLineInCart?.DeliveryOptions || [];

    const deliveryOptionSelected = deliveryOptionsList.find((option) => option?.ExternalId
      === firstLineInCart?.DeliveryOptionId);
    if (deliveryOptionSelected?.ShippingOptionType?.Value === DELIVERY_OPTION.PickUpStore) {
      dispatch(toggleSubmitOrderLoading(true));
      try {
        const response = await getListStoresOutOfStock();
        if (response?.data && response.data.Success) {
          if (response.data.ErrorMessage) throw new Error(response.data.ErrorMessage);
          if (response.data.ListStoreProductOutOfStock?.length) {
            const lines = cartValue?.Lines || [];
            const listOutOfStockProduct = [...response.data.ListStoreProductOutOfStock];
            const outOfStockProductNames = [];
            for (const item of listOutOfStockProduct) {
              const productData = lines.find((line) => line?.ProductId
                === item?.ProductId
                && line?.VariantId
                === item?.VariantId);
              if (productData) outOfStockProductNames.push(productData.DisplayName || '');
            }
            if (outOfStockProductNames.length) {
              throw new Error(settingGlobal?.['StoreOutOfProductText']?.replace(
                '{storeName}',
                firstLineInCart?.Address?.AddressName || ''
              )
                ?.replace('{listProduct}', outOfStockProductNames.join(', ')));
            }
          }

          callback();
        }
      } catch (error) {
        dispatch(submitOrderFailed([error?.message || 'Failed']));
      } finally {
        dispatch(toggleSubmitOrderLoading(false));
      }
    } else {
      callback();
    }
  }, [cartValue, getListStoresOutOfStock]);
};

export default useCheckingBOPISOrder;